import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { map, includes } from 'lodash';
import { useMemberListContext } from '@frontend/app/context/MemberListContext';
import { useMemberFieldSchemasQuery, useMemberSearchQuery } from '@frontend/app/hooks';
import { useMemberFieldsWithSources } from '@frontend/app/hooks/memberList';
import { useMessagingContext } from '@frontend/hooks';
import { useParams } from 'react-router-dom';

import { IMemberSearchQuery } from '@frontend/app/types/MemberSearch';
import { IColumn, IColumnVisibility } from '@frontend/app/types/Columns';
import { MemberSearchQuery_members as IMember } from '@frontend/app/queries/types/MemberSearchQuery';
import { ITableColumnConfig } from '@components';
import { TProject } from '@frontend/app/containers/Projects/types';
import { renderSocialProfileCell } from './CustomCells/SocialProfileCell';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shadcn/components/ui/tooltip';
import { Button as ShadcnButton } from '@/shadcn/components/ui/button';
import DataTable from '@/app/refresh-components/DataTable';

import { useMemberSearchQueryVariables } from './hooks';
import { defaultRenderCell } from './utils/defaultRenderCell';
import { createApplicationTemplateColumns } from './utils/createApplicationTemplateColumns';

const { useMemo: ReactUseMemo } = React;

// Safe wrapper for ProspectsListToolbar that handles context availability
const SafeProspectsListToolbar = React.lazy(() =>
  import('@/app/containers/Projects/ProjectsPage/ProspectsList/ProspectsListToolbar').then((module) => ({
    default: () => {
      try {
        return <module.ProspectsListToolbar />;
      } catch (error) {
        console.warn('MemberListContext not available for toolbar:', error);
        return null;
      }
    }
  }))
);

interface IMemberTableFlexProps {
  columns: IColumn[];
  columnVisibility: IColumnVisibility;
  searchQuery: IMemberSearchQuery;
  showCheckboxColumn?: boolean;
  showMembersChangedNotice?: boolean;
  headerActions?: (refetchData?: () => void) => React.ReactElement;
  tableHeader?: React.ReactElement;
  membersHaveChanged: boolean;
  onSelectedMembersChange?: (selectedMembers: IMember[]) => void;
  onSortDirChange?: (columnField: string, sortDir: string) => void;
  onRefreshClick: () => void;
  onDismissMembersHaveChangedNotice: () => void;
  renderCell?: (column: IColumn) => ITableColumnConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchProjectCounts?: () => Promise<any>;
  refetchData: () => Promise<void>;
  project?: TProject;
}

// Helper component for cell content with ellipsis and tooltip
const CellWithTooltip: React.FC<{ content: React.ReactNode; isText?: boolean }> = ({ content, isText = false }) => {
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const element = textRef.current;
    if (element && isText) {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, [content, isText]);

  if (!isText || !isOverflowing) {
    return (
      <div ref={textRef} className="truncate">
        {content}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div ref={textRef} className="truncate cursor-help">
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const MemberTableFlex: React.FC<IMemberTableFlexProps> = ({
  columns,
  columnVisibility,
  searchQuery,
  showCheckboxColumn = false,
  showMembersChangedNotice = false,
  headerActions,
  tableHeader,
  membersHaveChanged,
  onSelectedMembersChange,
  onSortDirChange,
  onRefreshClick,
  onDismissMembersHaveChangedNotice,
  renderCell,
  refetchProjectCounts,
  refetchData,
  project,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [toggleColumns, setToggleColumns] = useState<{ [key: string]: boolean }>({
    social: false,
  });
  const { showError } = useMessagingContext();
  const { projectId } = useParams<{ projectId: string }>();
  const programId = projectId ? parseInt(projectId, 10) : undefined;

  // Get context data for pagination and pageSize
  const { page, pageSize, updateCount } = useMemberListContext();

  // Get member fields with sources
  const { fields: fieldsWithSources } = useMemberFieldsWithSources(undefined, programId, true);
  const { data: { schemas: memberFieldSchemas = null } = {} } = useMemberFieldSchemasQuery();

  // Define social media fields
  const socialMediaFields = useMemo(() => {
    const socialFieldNames = ['Instagram', 'TikTok', 'YouTube', 'Twitter', 'Facebook', 'LinkedIn', 'Snapchat'];
    return fieldsWithSources.filter((field) =>
      includes(socialFieldNames, field.headerName) &&
      field.source === 'Default Member Data'
    );
  }, [fieldsWithSources]);

  const baseMemberSearchQueryVariables = useMemberSearchQueryVariables(
    searchQuery,
    columnVisibility,
    pageSize,
  );

  // For flex UI, we need a separate query that handles pagination at the query level
  const flexUIQueryVariables = useMemo(() => {
    const currentPage = page !== undefined ? page : 0;
    return {
      ...baseMemberSearchQueryVariables,
      skip: (currentPage - 1) * pageSize,
      take: pageSize,
    };
  }, [baseMemberSearchQueryVariables, pageSize, page]);

  const skipLoadingMembers = useMemo(() => !searchQuery || !columnVisibility, [
    searchQuery,
    columnVisibility,
  ]);

  const {
    error: flexError,
    data: { members: flexMembersList = null } = {},
    refetch: refetchFlexMembers,
  } = useMemberSearchQuery({
    variables: flexUIQueryVariables,
    skip: skipLoadingMembers,
    fetchPolicy: 'cache-first',
  });

  const applicationTemplateColumns = createApplicationTemplateColumns(project, columns, memberFieldSchemas);

  useEffect(() => {
    if (flexMembersList) {
      updateCount(flexMembersList.length);
    }
  }, [flexMembersList, updateCount]);

  // Handle flex UI errors
  useEffect(() => {
    if (flexError) {
      showError(flexError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flexError]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataTableColumns = ReactUseMemo<ColumnDef<any>[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processedColumns: ColumnDef<any>[] = [];

    // Create a set of social media field IDs to filter out from regular columns
    const socialFieldIds = new Set(socialMediaFields.map((field) => field.field));

    columns.forEach((column) => {
      // Skip social media fields as they'll be handled by the social column
      if (socialFieldIds.has(column.field)) {
        return;
      }

      // Add the regular column
      processedColumns.push({
        id: column.field,
        accessorKey: column.field,
        header: () => (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">{column.headerName}</span>
          </div>
        ),
        cell: ({ row, column: tableColumn }) => {
          const columnConfig = renderCell
            ? renderCell(column)
            : defaultRenderCell(column, { refetchProjectCounts });

          if (columnConfig.render) {
            const result = columnConfig.render(row.original[tableColumn.id], {
              isEditing: false,
              isRowSelected: false,
              isCellSelected: false,
              onToggleEditing: () => {
                // No-op for flex table
              },
              onChangeCellValue: () => {
                // No-op for flex table
              },
              onToggleRowSelected: () => {
                // No-op for flex table
              },
              onToggleShift: () => {
                // No-op for flex table
              },
            });
            return result !== undefined && result !== null ? (
              <CellWithTooltip content={result} isText={typeof result === 'string'} />
            ) : null;
          }

          const cellValue = row.original[tableColumn.id];
          const displayValue = cellValue !== undefined && cellValue !== null ? String(cellValue) : null;

          return displayValue ? (
            <CellWithTooltip content={displayValue} isText />
          ) : null;
        },
        enableSorting: true,
        size: column.field === 'name' ? 350 : 250,
      });

      // Add social column after name column
      if (column.field === 'name' && socialMediaFields.length > 0) {
        const isSocialExpanded = toggleColumns.social || false;

        // Add the main social column
        processedColumns.push({
          id: 'social',
          accessorKey: 'social',
          header: () => (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">Social Accounts</span>
            </div>
          ),
          cell: ({ row }) => {
            const member = row.original;
            const visibleSocialFields = socialMediaFields.filter((field) =>
              member.fields && member.fields[field.field] && member.fields[field.field].trim() !== ''
            );

            if (visibleSocialFields.length === 0) {
              return <span className="text-gray-400">-</span>;
            }

            // Show up to 2 social account platform names
            const displayFields = visibleSocialFields.slice(0, 2);
            const remainingCount = visibleSocialFields.length - 2;

            return (
              <div className="flex gap-2">
                {displayFields.map((field) => (
                  <span
                    key={field.field}
                    className="bg-gray-100 text-gray-800 rounded-full px-3 py-1 text-xs font-medium"
                  >
                    {field.headerName}
                  </span>
                ))}
                {remainingCount > 0 && (
                  <span className="bg-gray-100 text-gray-800 rounded-full px-3 py-1 text-xs font-medium">
                    +{remainingCount}
                  </span>
                )}
              </div>
            );
          },
          enableSorting: false,
          size: 250,
          meta: {
            isColumnExpanded: isSocialExpanded,
            isExpandableColumn: true,
            onToggleClick: () => {
              setToggleColumns((prev) => ({
                ...prev,
                social: !prev.social,
              }));
            },
            headerGroupBorder: {
              borderColor: '#3B82F6', // Blue color for social media column
            },
          },
        });

        // Add sub-columns when expanded
        if (isSocialExpanded) {
          socialMediaFields.forEach((field) => {
            processedColumns.push({
              id: `social_${field.field}`,
              accessorKey: `social_${field.field}`,
              header: () => (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{field.headerName}</span>
                </div>
              ),
              cell: ({ row }) => {
                const member = row.original;
                const value = member.fields && member.fields[field.field];

                if (!value || value.trim() === '') {
                  return <span className="text-gray-400">-</span>;
                }

                // Use SocialProfileCell for social media fields to enable click-to-modal functionality
                const socialProfileData = {
                  username: value,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  network: field.headerName.toLowerCase() as any,
                  refetchProjectCounts,
                };

                const socialProfileContext = {
                  isEditing: false,
                  isRowSelected: false,
                  isCellSelected: false,
                  onToggleEditing: () => {
                    // No-op for flex table
                  },
                  onChangeCellValue: () => {
                    // No-op for flex table
                  },
                  onToggleRowSelected: () => {
                    // No-op for flex table
                  },
                  onToggleShift: () => {
                    // No-op for flex table
                  },
                  rowData: member,
                };

                return renderSocialProfileCell(socialProfileData, socialProfileContext);
              },
              enableSorting: false,
              size: 150,
              meta: {
                greyBackground: true,
                headerGroupBorder: {
                  borderColor: '#3B82F6', // Same blue color for sub-columns
                },
              },
            });
          });
        }
      }
    });

    return [...processedColumns, ...applicationTemplateColumns];
  }, [columns, renderCell, refetchProjectCounts, socialMediaFields, toggleColumns, applicationTemplateColumns]);

  const handleDataTableSelectionChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (selectedRows: any[]) => {
      const selectedMemberObjects = selectedRows.map((row) => row._raw);
      if (onSelectedMembersChange) {
        onSelectedMembersChange(selectedMemberObjects);
      }
    },
    [onSelectedMembersChange],
  );

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      setSorting(newSorting);

      if (onSortDirChange && newSorting.length > 0) {
        const sort = newSorting[0];
        onSortDirChange(sort.id, sort.desc ? 'desc' : 'asc');
      }
    },
    [sorting, onSortDirChange],
  );

  // Update refetch data to use flex refetch
  const handleRefetchData = useCallback(async () => {
    await refetchFlexMembers();
    await refetchData();
  }, [refetchFlexMembers, refetchData]);

  // Transform members data for table
  const tableData = useMemo(() => {
    if (!flexMembersList) return [];

    return map(flexMembersList, (member) => ({
      ...member,
      ...member?.fields,
      name: {
        id: member.id,
        name: member.name,
        profile: member.profilePicture,
      },
      id: String(member.id),
      _raw: member,
    }));
  }, [flexMembersList]);

  return (
    <div className="space-y-4">
      {showMembersChangedNotice && membersHaveChanged && (
        <div className="rounded-lg border border-border bg-blue-1 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              Member data are updated. Click Refresh to see the updated data
            </span>
            <div className="flex gap-2">
              <ShadcnButton
                variant="default"
                size="sm"
                onClick={onRefreshClick}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Refresh
              </ShadcnButton>
              <ShadcnButton
                variant="outline"
                size="sm"
                onClick={onDismissMembersHaveChangedNotice}
              >
                Dismiss
              </ShadcnButton>
            </div>
          </div>
        </div>
      )}

      {tableHeader}

      <div className="flex items-center justify-between px-6">
        {headerActions(handleRefetchData)}
        <React.Suspense fallback={null}>
          <SafeProspectsListToolbar />
        </React.Suspense>
      </div>

      <DataTable
        columns={dataTableColumns}
        data={tableData}
        sortable
        selectable={showCheckboxColumn}
        onSortingChange={handleSortingChange}
        sorting={sorting}
        draggable={false}
        maxHeight="calc(100vh - 240px)"
        bordered
        wrapperClassName="rounded-lg border border-border bg-card"
        tableContentClassName="bg-background"
        clearSelection={false}
        onSelectionChange={handleDataTableSelectionChange}
      />
    </div>
  );
};
